import { useRef, useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useSwitchActiveWalletChain,
} from "thirdweb/react";
import {
  createThirdwebClient,
  getContract,
  prepareContractCall,
  sendTransaction,
} from "thirdweb";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
import { useHttp } from "../hooks/useHttp";
import { logger } from "../utils/logger";
import { getMewToyImageUrl } from "../utils/mew";
import { MEW_CHAIN, MEW_CHAIN_ID, NS_CHAIN_ID } from "../config/chain";

const carouselCards = [
  {
    id: 1,
    image: "/slider/mew-claim/1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the Mew tag. A page will open, click <span class='font-medium text-black'>Claim now</span> or <span class='font-medium text-black'>Sign Up</span>.",
  },
  {
    id: 2,
    image: "/slider/mew-claim/2.png",
    title: "Connect & confirm",
    description:
      "Choose your wallet app and connect it. When asked, <span class='font-medium text-black'>approve the connection</span>.",
  },
  {
    id: 3,
    image: "/slider/mew-claim/3.png",
    title: "Claim your digital collectible",
    description:
      "The collectible preview will appear. Tap Claim and your Mew collectible is now yours!",
  },
];

// Drawer types for the flow
type DrawerType =
  | "loading"
  | "wallet_connect"
  | "scan_chip"
  | "scan_again"
  | "claim_nft"
  | "claiming"
  | "success"
  | "already_claimed"
  | "error"
  | null;

// API Response interface
interface MewClaimResponse {
  success: boolean;
  action: "claim" | "already_claimed" | "wallet_not_connected";
  uid?: string;
  timestamp?: number;
  tokenType?: number;
  signature?: string;
  message: string;
}

export const ClaimMew = () => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  // Wallet hooks
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const address = account?.address;
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      account
    );
  }, [wallet, account]);
  const eoaAddress = eoaAccount?.address;
  const activeWalletChain = useActiveWalletChain();
  const switchActiveWalletChain = useSwitchActiveWalletChain();

  const uid = searchParams.get("uid");

  // Check if user came with NFC scan parameters
  const hasNfcParams = !!uid;

  // State management
  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);
  const [mewClaimResponse, setMewClaimResponse] =
    useState<MewClaimResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  // Contract configuration
  const contractAddress =
    process.env.REACT_APP_MEW_CONTRACT_ADDRESS ||
    process.env.REACT_APP_CONTRACT_ADDRESS;
  const requiredChain = MEW_CHAIN;

  const settings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => {
      setCurrentSlide(current);
    },
  };

  // Validate UID and call backend API
  useEffect(() => {
    let isMounted = true;

    const validateAndCallAPI = async () => {
      // Validate UID parameter
      if (!uid) {
        logger.ui("Mew claim page loaded without UID parameter");
        return;
      }

      // Validate UID format
      if (!/^[A-F0-9]+$/i.test(uid)) {
        if (isMounted) {
          setError("Invalid UID format");
          setCurrentDrawer("error");
        }
        return;
      }

      if (!address) {
        if (isMounted) {
          setCurrentDrawer("wallet_connect");
        }
        return;
      }

      // If wallet is connected but on wrong chain, auto-switch will handle it
      // Continue with API call - the chain check happens in the claim function
      // Use smart account address for backend API call (gasless transaction)
      // Call backend API
      if (!isMounted) return;
      setCurrentDrawer("loading");
      setError(null);

      try {
        // Use EOA address for signature generation to match the contract call
        const addressForSigning = eoaAddress || address;
        logger.api("Calling /mew_claim API", {
          uid,
          address: addressForSigning,
        });

        const { data } = await sendRequest<MewClaimResponse>({
          endpoint: `/api/mew_claim?uid=${encodeURIComponent(
            uid as string
          )}${addressForSigning ? `&address=${encodeURIComponent(addressForSigning)}` : ""}`,
          method: "GET",
          responseType: "json",
        });

        if (!isMounted) return;
        logger.api("Mew claim API response received", { response: data });
        setMewClaimResponse(data);

        if (!data.success) {
          setError(data.message);
          if (data.action === "already_claimed") {
            setCurrentDrawer("already_claimed");
          } else {
            setCurrentDrawer("error");
          }
          return;
        }

        switch (data.action) {
          case "claim":
            setCurrentDrawer("claim_nft");
            break;
          case "wallet_not_connected":
            setCurrentDrawer("wallet_connect");
            break;
          case "already_claimed":
            setError(
              data.message || "This Mew NFT has already been claimed"
            );
            setCurrentDrawer("already_claimed");
            break;
          default:
            setError("Unknown response from server");
            setCurrentDrawer("error");
        }
      } catch (err: any) {
        if (!isMounted) return;
        if (err?.name === "AbortError" || err?.message?.includes("abort")) {
          logger.ui(
            "Request aborted (component unmounted or new request started)"
          );
          return;
        }
        logger.apiError("Mew claim API call error", err as Error);
        setError(
          err instanceof Error ? err.message : "Failed to validate UID"
        );
        setCurrentDrawer("error");
      }
    };

    validateAndCallAPI();

    return () => {
      isMounted = false;
    };
  }, [uid, address, eoaAddress, sendRequest]);

  // When wallet connects without UID, show scan_again drawer
  useEffect(() => {
    if (address && currentDrawer === "wallet_connect" && !hasNfcParams) {
      setCurrentDrawer("scan_again");
    }
  }, [address, currentDrawer, hasNfcParams]);

  // When wallet connects, retry API call
  useEffect(() => {
    if (address && currentDrawer === "wallet_connect" && uid) {
      // Retry the API call with EOA address to match the contract call
      const retryCall = async () => {
        setCurrentDrawer("loading");
        setError(null);

        try {
          // Use EOA address for signature generation to match the contract call
          const addressForSigning = eoaAddress || address;
          const { data } = await sendRequest<MewClaimResponse>({
            endpoint: `/api/mew_claim?uid=${encodeURIComponent(
              uid as string
            )}&address=${encodeURIComponent(addressForSigning)}`,
            method: "GET",
            responseType: "json",
          });

          setMewClaimResponse(data);

          if (!data.success) {
            setError(data.message);
            if (data.action === "already_claimed") {
              setCurrentDrawer("already_claimed");
            } else {
              setCurrentDrawer("error");
            }
            return;
          }

          if (data.action === "claim") {
            setCurrentDrawer("claim_nft");
          } else if (data.action === "already_claimed") {
            setError(data.message || "Already claimed");
            setCurrentDrawer("already_claimed");
          }
        } catch (err: any) {
          logger.apiError("Mew claim API retry error", err as Error);
          setError(
            err instanceof Error ? err.message : "Failed to validate UID"
          );
          setCurrentDrawer("error");
        }
      };

      retryCall();
    }
  }, [address, eoaAddress, currentDrawer, uid, sendRequest]);

  useEffect(() => {
    if (currentDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  // Auto-switch to Avalanche chain if wallet is connected but on wrong chain
  useEffect(() => {
    const autoSwitchToAvalanche = async () => {
      // Only auto-switch if:
      // 1. Wallet is connected
      // 2. Active chain is not Avalanche
      // 3. We have switch function available
      // 4. Not already switching
      if (
        address &&
        activeWalletChain &&
        activeWalletChain.id !== MEW_CHAIN_ID &&
        activeWalletChain.id !== NS_CHAIN_ID &&
        switchActiveWalletChain &&
        !isSwitchingChain
      ) {
        console.log("autoSwitchToAvalanche", activeWalletChain.id, MEW_CHAIN_ID);
        logger.wallet("Auto-switching to Avalanche chain", {
          currentChainId: activeWalletChain.id,
          targetChainId: MEW_CHAIN_ID,
        });

        try {
          setIsSwitchingChain(true);
          await switchActiveWalletChain(requiredChain);
          logger.wallet("Auto-switched to Avalanche chain successfully");
        } catch (err: any) {
          logger.walletError("Auto-switch to Avalanche failed", err as Error);
          // Don't show error for auto-switch failures, user can manually switch
          // The UI will show the switch network button if needed
        } finally {
          setIsSwitchingChain(false);
        }
      }
    };

    // Small delay to avoid race conditions with wallet connection
    const timeoutId = setTimeout(() => {
      autoSwitchToAvalanche();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    address,
    activeWalletChain?.id,
    switchActiveWalletChain,
    requiredChain,
  ]);

  // Handle network switching
  const handleSwitchNetwork = async () => {
    if (!switchActiveWalletChain) return;
    setIsSwitchingChain(true);
    setError(null);

    try {
      logger.wallet("Switching network", {
        chainId: MEW_CHAIN_ID,
      });
      await switchActiveWalletChain(requiredChain);
      logger.wallet("Network switched successfully");
      setError(null);
    } catch (err: any) {
      logger.walletError("Failed to switch network", err as Error);

      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        setError(
          "Network switch cancelled. Please switch to Avalanche to continue."
        );
      } else {
        setError(
          "Failed to switch network. Please switch manually in your wallet."
        );
      }
    } finally {
      setIsSwitchingChain(false);
    }
  };

  // Handle NFT claim via smart contract
  const handleClaimNFT = async () => {
    if (
      !address ||
      !account ||
      !mewClaimResponse ||
      mewClaimResponse.action !== "claim"
    ) {
      return;
    }

    const { uid: claimUid, timestamp, tokenType, signature } = mewClaimResponse;

    if (!claimUid || signature == null || timestamp == null || tokenType == null) {
      setError("Missing claim parameters");
      return;
    }

    // Check if on correct network
    if ((activeWalletChain?.id || 0) !== MEW_CHAIN.id) {
      setError("Please switch to the target network");
      return;
    }

    setIsClaiming(true);
    setCurrentDrawer("claiming");
    setError(null);

    try {
      logger.contract("Loading ABI");
      const response = await fetch("/MewNftAbi.json");
      const MewNftAbi = await response.json();
      logger.contract("ABI loaded successfully");

      if (!contractAddress) {
        throw new Error("Contract address not configured");
      }

      const client = createThirdwebClient({
        clientId: process.env.REACT_APP_THIRDWEB_CLIENT_ID as string,
      });
      const chain = requiredChain;
      const contract = getContract({
        client,
        chain,
        address: contractAddress,
        abi: MewNftAbi,
      });

      logger.contract("Calling claim function", {
        uid: claimUid,
        timestamp,
        tokenType,
        signatureLength: signature.length,
      });

      if (!account) {
        throw new Error("No account available for transaction");
      }

      // Contract signature: claim(uint256 timestamp, uint256 tType, address caller, string uid, bytes signature)
      const eoaAddress = eoaAccount?.address || address;
      const transaction = prepareContractCall({
        contract,
        method: "claim",
        params: [timestamp, tokenType, eoaAddress, claimUid, signature],
      });

      const receipt = await sendTransaction({
        account,
        transaction,
      });

      const txHashResult = (receipt as any)?.transactionHash;
      setTxHash(txHashResult || null);
      logger.contract("Transaction submitted", { txHash: txHashResult });

      if (txHashResult) {
        logger.contract("Mew NFT claimed successfully", {
          txHash: txHashResult,
          uid: claimUid,
        });
        setCurrentDrawer("success");
      } else {
        throw new Error("Transaction failed");
      }
    } catch (err: any) {
      logger.contractError("Failed to claim Mew NFT", err as Error, {
        uid: mewClaimResponse?.uid,
        address,
        contractAddress,
      });

      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        setError("Transaction cancelled. Please try again.");
        setCurrentDrawer("claim_nft");
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to claim Mew NFT"
        );
        setCurrentDrawer("error");
      }
    } finally {
      setIsClaiming(false);
    }
  };

  console.log("activeWalletChain", activeWalletChain);

  return (
    <div className="py-5">
      <div className="px-6 mb-8">
        {/* Show landing image with Claim Now overlay if no UID */}
        {!uid && (
          <div className="rounded-[22px] overflow-hidden aspect-square relative">
            <img
              src="/images/mew/mew-landing.png"
              alt="Catch Mew If You Can"
              className="object-cover absolute inset-0 w-full h-full"
            />
            <div className="backdrop-blur-[5px] rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/25 flex justify-center">
              <button
                onClick={() => setCurrentDrawer("wallet_connect")}
                className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
              >
                Claim Now
              </button>
            </div>
          </div>
        )}

        {/* Show NFT preview if UID present */}
        {uid && (
          <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mb-4">
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 animate-pulse" />
            <img
              src="/images/mew/mew-landing.png"
              alt="Mew"
              className="object-cover absolute inset-0 w-full h-full"
            />
          </div>
        )}
      </div>

      <div className="px-6">
        <h3 className="mb-4 text-2xl font-bold text-center">How it Works</h3>

        <div className="relative carousel-container">
          <Slider ref={sliderRef} {...settings}>
            {carouselCards.map((card) => (
              <CarouselCard key={card.id} card={card} />
            ))}
          </Slider>
        </div>

        <div className="my-3 text-center">
          <h4 className="mb-2 text-xl font-bold">
            {carouselCards[currentSlide].title}
          </h4>

          <p
            className="text-lg leading-tight text-[#626262] whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: carouselCards[currentSlide].description,
            }}
          ></p>
        </div>

        <div className="flex gap-2 justify-center items-center mt-5 mb-6">
          {carouselCards.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${
                currentSlide === index
                  ? "w-[37px] h-[10px] bg-[#000000]"
                  : "w-[10px] h-[10px] bg-[#CCCCCC]"
              }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {/* Loading Drawer */}
      {currentDrawer === "loading" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">
                Validating UID
              </div>
              <div className="text-[#626262]">
                Please wait while we verify your Mew...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connect Drawer */}
      {currentDrawer === "wallet_connect" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center relative">
            <button
              type="button"
              onClick={() => setCurrentDrawer(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="mb-4">
              <img src="/svg/223-logo.svg" alt="223 Logo" />
            </div>

            <div className="text-center space-y-2.5 w-full">
              <div className="text-2xl font-bold text-black">Sign In</div>

              <div className="text-[#626262]">
                To claim your 223 Digital Collectible, sign in or create an
                account
              </div>

              <div className="!mt-6 flex justify-center">
                <ThirdwebWalletConnect showDropdown={false} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Chip Drawer */}
      {currentDrawer === "scan_chip" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/scan-chip.svg" alt="scan-chip" />

            <div className="mt-8 text-center">
              <div className="text-2xl font-bold text-black">
                Please scan the NFC chip
              </div>
              <div className="text-[#626262] mt-2">
                Tap your phone near the Mew tag to continue
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Again Drawer */}
      {currentDrawer === "scan_again" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-12 max-h-fit flex justify-center flex-col items-center">
            <img src="/images/mew/wallet-connected.png" alt="wallet-connected" className="max-w-[200px] mx-auto" />

            <div className="text-center space-y-2.5 mt-6">
              <div className="text-2xl font-bold text-black">
                Connected Successfully!
              </div>

              <div className="text-[#626262] leading-relaxed">
                Wallet connected successfully! Please{" "}
                <span className="font-semibold text-black">close this tab</span>{" "}
                and tap the NFC chip to complete your claim.
              </div>

              <button
                onClick={() => setCurrentDrawer(null)}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-2"
              >
                Close and tap again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim NFT Drawer */}
      {currentDrawer === "claim_nft" && mewClaimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="space-y-2.5">
              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400" />
                <img
                  src={getMewToyImageUrl(mewClaimResponse.tokenType)}
                  alt="Mew"
                  className="object-cover absolute inset-0 w-full h-full"
                />

                <div className="backdrop-blur-md rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/10">
                  <div className="max-w-[240px] mx-auto flex flex-col items-center justify-center gap-2">
                    <h3 className="text-lg font-bold text-center">
                      223 x Mew Authenticity Certificate
                    </h3>

                    {(activeWalletChain?.id || 0) === MEW_CHAIN_ID && (
                      <button
                        onClick={handleClaimNFT}
                        disabled={isClaiming}
                        className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 disabled:opacity-50"
                      >
                        Claim Now
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              {(activeWalletChain?.id || 0) !== MEW_CHAIN_ID && (
                <div>
                  <div className="px-4 py-3 mb-2 text-sm text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
                    Please switch to the target network
                  </div>

                  <button
                    onClick={handleSwitchNetwork}
                    disabled={isSwitchingChain}
                    className="py-2 w-full text-lg font-bold text-white bg-orange-600 transition-colors duration-200 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSwitchingChain ? "Switching..." : "Switch Network"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Claiming (Processing) Drawer */}
      {currentDrawer === "claiming" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/images/mew/wallet-connected.png" alt="claiming" className="max-w-[200px] mx-auto" />

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">Claiming</div>

              <div className="text-[#626262]">
                Claiming your Digital Collectible. Please wait.
              </div>

              {txHash && (
                <div className="px-4 text-xs text-gray-500 break-all">
                  Transaction:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {txHash.slice(0, 10)}...{txHash.slice(-8)}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Drawer */}
      {currentDrawer === "success" && mewClaimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="space-y-2 text-center">
              <div className="text-lg font-bold text-center">🎉 CONGRATS!</div>

              <div className="text-[#626262]">
                Congratulations! Your Mew Digital Collectible has been
                claimed.
              </div>

              {txHash && (
                <div className="px-4 text-xs text-gray-500 break-all">
                  Transaction:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {txHash.slice(0, 10)}...{txHash.slice(-8)}
                  </a>
                </div>
              )}

              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400" />
                <img
                  src={getMewToyImageUrl(mewClaimResponse.tokenType)}
                  alt="Mew"
                  className="object-cover absolute inset-0 w-full h-full"
                />
              </div>

              <div className="text-[#626262]">Certificate of Authenticity</div>

              <button
                onClick={() => navigate("/rewards/mew")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "already_claimed" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="text-center">
              <img
                src="/svg/wallet-connected.svg"
                alt="wallet-connected"
                className="mx-auto"
              />

              <h2 className="mb-2 text-xl font-bold">Already Claimed</h2>

              <p className="mb-6 text-gray-600">
                {error || "This Mew NFT has already been claimed"}
              </p>

              <button
                onClick={() => navigate("/rewards/mew")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Drawer */}
      {currentDrawer === "error" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img
              src="/svg/mew/mew-error.svg"
              alt="error"
              className="mb-4 w-32 h-32"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Something Went Wrong
              </div>

              <div className="text-[#626262]">
                {error || "An unexpected error occurred. Please try again."}
              </div>

              <button
                onClick={() =>
                  window.open("/support", "_blank", "noopener,noreferrer")
                }
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Contact Support
              </button>

              <div className="flex items-center justify-center gap-1 text-sm text-gray-500 !mt-4">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>

                <span>
                  Please contact support if you believe this is an error.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CarouselCard = ({
  card,
}: {
  card: { id: number; image: string; title: string; description: string };
}) => {
  return (
    <div className="w-full rounded-[14px] overflow-hidden bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 shadow-xl mx-auto">
      <div className="relative w-full h-full rounded-[14px] overflow-hidden aspect-[3/4]">
        <img
          src={card.image}
          alt={card.title}
          className="object-cover absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
};
